import qs from 'qs';
// eslint-disable-next-line import/no-duplicates
import { format, parseISO } from 'date-fns';
// eslint-disable-next-line import/no-duplicates
import { ptBR } from 'date-fns/locale';
import { AxiosRequestConfig, AxiosResponse } from 'axios';
import api from '~/utils/api';
import {
  RequestProposalObj,
  statusMapAdmin,
  statusMapCliente,
  TerminationRequestsContractView,
  TerminationRequestsContracVisualization,
  TerminationRequestsList,
  UpdateCalculation,
} from '~/types/terminationRequests';
import { formatInTimeZone } from '~/utils/formatToTimeZone';
import { formatCurrency } from '~/utils/formatCurrency';
import { Pagination } from '~/types';
import { PAGE_SIZE } from '~/constants';
import { QueryFilterContext } from '~/hooks/useFilter/types';

export async function getContractDetailsByNumber(
  nu_contrato: string[],
): Promise<TerminationRequestsContractView[]> {
  const params = {
    contratos: nu_contrato,
  };
  const { data } = await api.post<TerminationRequestsContractView[]>(
    `/rescisao/detalhe-contratos`,
    params,
  );

  const formattedData = data.map(ct => {
    return {
      ...ct,
      dt_inicialcontrato: ct.dt_inicialcontrato
        ? formatInTimeZone(`${ct.dt_inicialcontrato}`, 'dd/MM/yyyy')
        : '-',
      dt_encerramentocontrato: ct.dt_encerramentocontrato
        ? formatInTimeZone(`${ct.dt_encerramentocontrato}`, 'dd/MM/yyyy')
        : '-',
      vr_totalinicialcontrato: formatCurrency(
        Number(ct.vr_totalinicialcontrato),
      ),
      valor_mensal_atual: formatCurrency(Number(ct.valor_mensal_atual)).replace(
        'R$',
        '',
      ),
      total_faturas_vencidas: formatCurrency(Number(ct.total_faturas_vencidas)),
      equipamentos: ct.equipamentos.map(eq => ({
        ...eq,
        qtde_parcial: eq.qtde_total,
      })),
      faturas: ct?.faturas?.length
        ? ct?.faturas?.map(invoice => {
            return {
              ...invoice,
              nu_fatura: invoice.nu_fatura ?? '-',
              contratos: [],
              formattedValue: formatCurrency(Number(invoice.vr_fatura)),
              formattedStatus:
                invoice.st_pagamento === '1'
                  ? 'Pago'
                  : invoice.st_pagamento === '2'
                  ? 'Vencida'
                  : 'Em aberto',
              colorScheme:
                invoice.st_pagamento === '1'
                  ? 'green'
                  : invoice.st_pagamento === '2'
                  ? 'red'
                  : 'purple',
              formattedDueDate: formatInTimeZone(
                `${invoice.dt_vencimentofatura}T00:00:00Z`,
                'dd/MM/yyyy',
              ),
              formattedIssueDate: invoice.dt_emissaofatura
                ? format(parseISO(invoice.dt_emissaofatura), 'MMM/yyyy', {
                    locale: ptBR,
                  })
                : '-',
              status: Number(invoice.st_pagamento),
              formattedValoresContratos:
                invoice?.contrato_fatura_consolidada?.map(it => {
                  return formatCurrency(Number(it[0]));
                }),
            };
          })
        : [],
    };
  });

  return formattedData;
}

export async function getTerminationSimulationId(
  contract_ids: string[],
): Promise<any> {
  const { data } = await api.post<{ id_rescisao: number }>(
    `/rescisao/simular_rescisao`,
    {
      contratos: contract_ids,
    },
  );
  return data.id_rescisao;
}

export async function getTerminationById(
  id: string,
  analise: boolean,
  visualizacao_analise: boolean,
  simulation: boolean,
): Promise<TerminationRequestsContracVisualization> {
  const url = visualizacao_analise
    ? `/rescisao/${id}?visualizacao_analise=${visualizacao_analise}`
    : simulation
    ? `/rescisao/${id}?simulation=${simulation}`
    : `/rescisao/${id}?analise=${analise}`;
  const { data } = await api.get<TerminationRequestsContracVisualization>(url);
  const formattedData = {
    ...data,
    contratos: data.contratos.map(ct => ({
      ...ct,
      dt_inicialcontrato: ct.dt_inicialcontrato
        ? formatInTimeZone(`${ct.dt_inicialcontrato}`, 'dd/MM/yyyy')
        : '-',
      dt_encerramentocontrato: ct.dt_encerramentocontrato
        ? formatInTimeZone(`${ct.dt_encerramentocontrato}`, 'dd/MM/yyyy')
        : '-',
      vr_totalinicialcontrato: formatCurrency(
        Number(ct.vr_totalinicialcontrato),
      ),
      valor_mensal_atual: formatCurrency(Number(ct.valor_mensal_atual)).replace(
        'R$',
        '',
      ),
      total_faturas_vencidas: formatCurrency(Number(ct.total_faturas_vencidas)),
      equipamentos: data.equipamentos
        .filter(eq =>
          ct.equipamentos.some(
            ctEq =>
              ctEq.id_equipamento === eq.id_equipamento &&
              eq.contrato === ct.id_contrato,
          ),
        )
        .map(eq => ({
          ...eq,
          qtde_parcial: eq.qtde_parcial ?? eq.qtde_total,
        })),
      valor_multa: formatCurrency(Number(ct.valor_multa)),
      valor_total: formatCurrency(Number(ct.valor_total)),
    })),
  };
  return formattedData;
}

export async function postRequestProposal(
  obj: RequestProposalObj,
): Promise<TerminationRequestsContractView[]> {
  return api.post(`/rescisao`, obj);
}

export async function putRequestProposal(
  id: string,
  obj: RequestProposalObj,
  objCalculation: UpdateCalculation,
  contratos: TerminationRequestsContractView[],
  selectedFile: any,
  isSimulation?: boolean,
): Promise<TerminationRequestsContractView[]> {
  const formatedContratos = objCalculation.contratos.map(ct => ({
    id_contrato: ct.contrato,
    aviso_previo: ct.aviso_previo,
    multa: ct.multa,
    limite_dias: ct.limite_dias,
    valor_multa: ct.valor_multa.toFixed(2),
    valor_total: ct.valor_total.toFixed(2),
    valor_mensal_atual: ct.valor_mensal_atual.toFixed(2),
  }));
  const formData = new FormData();
  formData.append('contratos', JSON.stringify(formatedContratos));
  formData.append('descricao_motivo', obj.descricao_motivo);
  formData.append('motivo', obj.motivo);
  formData.append('desconto', objCalculation.desconto!.toFixed(2));
  formData.append('valor_total', objCalculation.valor_total!.toFixed(2));
  formData.append(
    'valor_total_geral',
    objCalculation.valor_total_geral!.toFixed(2),
  );
  if (!selectedFile) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    formData.append('arquivo', null);
  } else {
    formData.append('arquivo', selectedFile as Blob);
  }
  const config: AxiosRequestConfig = {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  };
  if (!isSimulation) {
    return api.put(`/rescisao/${id}`, formData, config);
  }
  return api.post(`/rescisao/${id}/efetivar_simulacao`, formData, config);
}

export async function getRequestContractTerminationList(
  page: number,
  search: string,
  status: string,
  solicitante: string,
  page_size?: number,
  query?: any,
  isAdmin?: boolean,
): Promise<Pagination<TerminationRequestsList>> {
  const { data } = await api.get<Pagination<TerminationRequestsList>>(
    '/rescisao',
    {
      params: {
        page,
        size: page_size || PAGE_SIZE,
        ...(search ? { search } : {}),
        ...(status ? { status } : {}),
        ...(solicitante ? { solicitante } : {}),
        ...{ ...query },
      },
      paramsSerializer: params => {
        return qs.stringify(params, { arrayFormat: 'repeat' });
      },
    },
  );

  const formatted = data.results.map(ct => ({
    ...ct,
    dt_solicitacao: formatInTimeZone(`${ct.dt_solicitacao}`, 'dd/MM/yyyy'),
    solicitanteColor: ct.solicitante === 1 ? 'green' : 'red',
    solicitanteStr: ct.solicitante === 1 ? 'CLIENTE' : 'POSITIVO',
    statusStr:
      !isAdmin || ct.solicitante === 0
        ? statusMapCliente[ct.status].label
        : statusMapAdmin[ct.status].label,
    statusColor:
      !isAdmin || ct.solicitante === 0
        ? statusMapCliente[ct.status].color
        : statusMapAdmin[ct.status].color,
    nu_contratos: ct.contratos.map(c => c).join(', '),
  }));

  return {
    ...data,
    results: formatted,
  };
}

export async function postCancelProposal(
  id: string,
  descricao_cancelar_rescisao: string,
): Promise<TerminationRequestsContractView[]> {
  return api.post(`/rescisao/${id}/cancelar_rescisao`, {
    descricao_cancelar_rescisao,
  });
}

export async function postUpdateCalculation(
  id: number,
  obj: UpdateCalculation,
): Promise<UpdateCalculation> {
  const { data } = await api.post(`/rescisao/${id}/atualizar_calculo`, obj);
  return data;
}

export async function downloadTerminationDoc(
  id: String,
  obj: UpdateCalculation,
): Promise<AxiosResponse<any>> {
  const url = `/rescisao/${id}/termo-rescisao`;
  return api.post(url, obj, {
    headers: {
      'Content-Type': 'application/docx',
    },
    responseType: 'blob' as 'json',
  });
}

export const generateTerminationToken = async (id: number): Promise<any> => {
  const { data } = await api.get<any>(`rescisao/${id}/gerar_token`);
  return data;
};

export const verifyTerminationToken = async (
  id: number,
  codigo: string,
): Promise<any> => {
  const { data } = await api.get<any>(
    `rescisao/${id}/verifica_token?token=${codigo}`,
  );
  return data;
};

export async function downloadTerminationPdf(
  id: number,
): Promise<AxiosResponse<any>> {
  const url = `/rescisao/${id}/termo-rescisao-pdf`;
  return api.get(url, {
    headers: {
      'Content-Type': 'application/pdf',
    },
    responseType: 'blob' as 'json',
  });
}

export async function downloadListXls(
  search = '',
  status: string,
  solicitante: string,
  query?: QueryFilterContext,
): Promise<AxiosResponse<any>> {
  const url = '/rescisao/xlsx';
  const groups = query?.groups?.map(e => e.value);
  const headoffices = query?.headOffices?.map(e => e.value);
  const branches = query?.branches?.map(e => e.value);
  return api.get(url, {
    params: {
      ...(search && { search }),
      ...(status && { status }),
      ...(solicitante && { solicitante }),
      ...(groups && { groups }),
      ...(headoffices && { headoffices }),
      ...(branches && { branches }),
    },
    paramsSerializer: params => {
      return qs.stringify(params, { arrayFormat: 'repeat' });
    },
    headers: {
      'Content-Type': 'application/xlsx',
    },
    responseType: 'blob' as 'json',
  });
}
